import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { chatApi, type ChatMessageView, type ChatThreadView } from '../api/chat'
import { useChatSocket } from '../api/hooks/useChatSocket'
import { queryKeys } from '../api/queryKeys'
import { useAuth } from '../auth/AuthContext'

/**
 * Универсальный чат: sidebar threads (слева) + messages selected thread (справа).
 * Используется и для роли TEACHER, и для роли PARENT — данные одни и те же,
 * различается только peerLabel и emptyHint.
 */
export function ChatView({
  peerLabel,
  emptyHint,
  initialThreadId,
}: {
  peerLabel?: string
  emptyHint?: string
  initialThreadId?: string
}) {
  const { t } = useTranslation()
  const resolvedPeerLabel = peerLabel ?? t('chatView.peerDefault')
  const resolvedEmptyHint = emptyHint ?? t('chatView.emptyHintDefault')
  const { teacher } = useAuth()
  const qc = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(initialThreadId ?? null)

  const threads = useQuery({
    queryKey: queryKeys.chat.threads(),
    queryFn: chatApi.listThreads,
    refetchInterval: false,
  })

  const messages = useQuery({
    queryKey: queryKeys.chat.messages(selectedId),
    queryFn: () => chatApi.listMessages(selectedId!),
    enabled: !!selectedId,
  })

  // WS — слушаем входящие, обновляем кэш
  useChatSocket(!!teacher, (msg: ChatMessageView) => {
    qc.setQueryData<ChatMessageView[]>(queryKeys.chat.messages(msg.threadId), (prev) => {
      if (!prev) return [msg]
      if (prev.some((m) => m.id === msg.id)) return prev
      return [...prev, msg]
    })
    qc.invalidateQueries({ queryKey: queryKeys.chat.threads() })
  })

  const selectedThread = useMemo(
    () => threads.data?.find((t) => t.id === selectedId) ?? null,
    [threads.data, selectedId],
  )

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-3 h-[calc(100vh-220px)] min-h-[400px]">
      {/* Sidebar */}
      <aside className={`bg-white border border-gray-200 rounded-xl overflow-y-auto ${selectedId ? 'hidden md:block' : ''}`}>
        {threads.isLoading && <div className="p-4 text-center text-gray-500 text-sm">{t('common.loading')}</div>}
        {threads.data?.length === 0 && (
          <div className="p-4 text-sm text-gray-500">{resolvedEmptyHint}</div>
        )}
        {threads.data?.map((thread) => (
          <ThreadRow
            key={thread.id}
            thread={thread}
            peerLabel={resolvedPeerLabel}
            isSelected={thread.id === selectedId}
            onClick={() => setSelectedId(thread.id)}
            t={t}
          />
        ))}
      </aside>

      {/* Messages */}
      <section className={`bg-white border border-gray-200 rounded-xl flex flex-col ${!selectedId ? 'hidden md:flex' : 'flex'}`}>
        {selectedThread ? (
          <ThreadConversation thread={selectedThread} messages={messages.data ?? []} onBack={() => setSelectedId(null)} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
            {t('chatView.selectThread')}
          </div>
        )}
      </section>
    </div>
  )
}

function ThreadRow({ thread, peerLabel, isSelected, onClick, t }: {
  thread: ChatThreadView
  peerLabel: string
  isSelected: boolean
  onClick: () => void
  t: TFunction
}) {
  return (
    <button
      onClick={onClick}
      className={`block w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
        isSelected ? 'bg-primary-soft' : ''
      }`}
    >
      <div className="flex items-baseline gap-2 mb-0.5">
        <div className={`font-medium text-sm truncate ${isSelected ? 'text-primary' : 'text-gray-900'}`}>
          {thread.peerName}
        </div>
        {thread.unreadCount > 0 && (
          <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500 truncate">
        {peerLabel === 'Ученик' ? '' : t('chatView.byPrefix')}{thread.studentName}
      </div>
      <div className="text-[10px] text-gray-400 mt-0.5">{formatRel(thread.lastMessageAt, t)}</div>
    </button>
  )
}

function ThreadConversation({
  thread,
  messages,
  onBack,
}: {
  thread: ChatThreadView
  messages: ChatMessageView[]
  onBack: () => void
}) {
  const { t } = useTranslation()
  const { teacher } = useAuth()
  const [text, setText] = useState('')
  const scrollRef = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()

  const send = useMutation({
    mutationFn: () => chatApi.sendMessage({ threadId: thread.id, text: text.trim() }),
    onSuccess: (msg) => {
      setText('')
      qc.setQueryData<ChatMessageView[]>(queryKeys.chat.messages(thread.id), (prev) =>
        prev && !prev.some((m) => m.id === msg.id) ? [...prev, msg] : (prev ?? [msg]),
      )
      qc.invalidateQueries({ queryKey: queryKeys.chat.threads() })
    },
  })

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages.length])

  return (
    <>
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
        <button onClick={onBack} className="md:hidden text-gray-500 hover:text-primary text-lg leading-none">←</button>
        <div className="min-w-0">
          <div className="font-semibold text-gray-900 truncate">{thread.peerName}</div>
          <div className="text-xs text-gray-500 truncate">{t('chatView.byStudent', { name: thread.studentName })}</div>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50/50">
        {messages.length === 0 && (
          <div className="text-center text-sm text-gray-400 py-10">{t('chatView.noMessages')}</div>
        )}
        {messages.map((m) => {
          const mine = m.senderId === teacher?.id
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm shadow-sm ${
                mine ? 'bg-primary text-white rounded-br-md' : 'bg-white text-gray-900 rounded-bl-md border border-gray-100'
              }`}>
                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                <div className={`text-[10px] mt-1 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                  {formatTime(m.createdAt)}
                  {mine && (
                    <span className="ml-1">{m.readAt ? '✓✓' : '✓'}</span>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); if (text.trim()) send.mutate() }}
        className="border-t border-gray-200 p-2 flex gap-2"
      >
        <input
          className="input-field flex-1"
          placeholder={t('chatView.messagePlaceholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={send.isPending}
        />
        <button type="submit" disabled={!text.trim() || send.isPending} className="btn-primary text-sm">
          {t('chatView.send')}
        </button>
      </form>
    </>
  )
}

function formatTime(ts: number): string {
  return new Date(ts).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })
}

function formatRel(ts: number, t: TFunction): string {
  const diff = Date.now() - ts
  if (diff < 60_000) return t('chatView.justNow')
  if (diff < 3600_000) return t('chatView.minShort', { n: Math.floor(diff / 60_000) })
  if (diff < 86400_000) return t('chatView.hourShort', { n: Math.floor(diff / 3600_000) })
  return new Date(ts).toLocaleDateString('ru')
}
